import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { APIHOST } from '../../common';

interface DisplayCreditCardProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
}

export default function DisplayCreditCard({ open, onClose, orderId }: DisplayCreditCardProps) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessToken2, setAccessToken2] = useState<string | null>(null);
  const [locator, setLocator] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && orderId) {
      generateAccessToken();
    }
  }, [open, orderId]);

  const generateAccessToken = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // First token request
      const response1 = await fetch(`${APIHOST}/api/orders/${orderId}/generateAccessToken`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response1.ok) {
        throw new Error('Failed to generate first access token');
      }

      const data1 = await response1.json();
      setAccessToken(data1.accessToken);
      setLocator(data1.locator);

      // Second token request
      const response2 = await fetch(`${APIHOST}/api/orders/${orderId}/generateAccessToken`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response2.ok) {
        throw new Error('Failed to generate second access token');
      }

      const data2 = await response2.json();
      setAccessToken2(data2.accessToken);
    } catch (err) {
      console.error('Error generating access token:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate access token');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAccessToken(null);
    setAccessToken2(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px', backgroundColor: "#FFFFFF" }
      }}
    >
      <DialogTitle>Credit Card Information</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {accessToken && accessToken2 && !loading && (
          <Box sx={{ width: '100%', height: '500px' }}>
            <iframe
              src={`https://service.pcibooking.net/api/payments/paycard/${locator}/display?accessToken=${accessToken}&accessToken2=${accessToken2}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '4px'
              }}
              title="Credit Card Display"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}